import { useEffect, useMemo, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { api, formatApiError } from "../../lib/api";

const MAX = 10 * 1024 * 1024;
const field = "h-11 w-full rounded-2xl border border-white/10 bg-white/[0.04] px-4 text-sm text-zinc-100 outline-none transition-colors placeholder:text-zinc-500 focus:border-gold/60";

export const UploadDialog = ({ open, onOpenChange, copy, defaultKind, onUploaded }) => {
  const u = copy.upload;
  const [kind, setKind] = useState(defaultKind);
  const [files, setFiles] = useState([]);
  const [caption, setCaption] = useState("");
  const [author, setAuthor] = useState("");
  const [progress, setProgress] = useState(null);
  const previews = useMemo(() => files.map((f) => ({ f, url: URL.createObjectURL(f) })), [files]);

  useEffect(() => () => { previews.forEach((p) => URL.revokeObjectURL(p.url)); }, [previews]);
  useEffect(() => {
    if (open) { setKind(defaultKind); setFiles([]); setCaption(""); setAuthor(""); setProgress(null); }
  }, [open, defaultKind]);

  const pick = (e) => {
    const list = Array.from(e.target.files || []);
    const ok = list.filter((f) => f.size <= MAX);
    if (ok.length < list.length) toast.error(u.tooBig);
    setFiles(ok);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) return;
    let done = 0;
    setProgress(0);
    for (const f of files) {
      const body = new FormData();
      body.append("file", f);
      body.append("kind", kind);
      body.append("caption", caption);
      body.append("author", author);
      try {
        await api.post("/media", body);
        done += 1;
        setProgress(done);
      } catch (err) {
        toast.error(`${u.failed}: ${formatApiError(err.response?.data?.detail)}`);
      }
    }
    if (done) { toast.success(u.done); onUploaded(); onOpenChange(false); }
    setProgress(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="results-upload-dialog" data-lenis-prevent className="max-h-[92svh] max-w-lg overflow-y-auto rounded-[28px] border-white/10 bg-surface text-zinc-100">
        <DialogTitle className="font-display text-xl">{u.title}</DialogTitle>
        <DialogDescription className="sr-only">{u.title}</DialogDescription>
        <form onSubmit={submit} className="mt-2 space-y-4">
          <div>
            <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">{u.kind}</span>
            <div className="grid grid-cols-2 gap-2">
              {["result", "testimonial"].map((k) => (
                <button type="button" key={k} onClick={() => setKind(k)} data-testid={`results-upload-kind-${k}`}
                  className={`h-11 rounded-full border text-sm font-semibold transition-colors ${kind === k ? "border-gold bg-gold text-ink" : "border-white/10 text-zinc-300 hover:border-gold/40"}`}>
                  {copy.tabs[k]}
                </button>
              ))}
            </div>
          </div>
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[22px] border border-dashed border-gold/40 bg-gold/[0.04] px-4 py-6 text-center text-sm text-zinc-300 transition-colors hover:bg-gold/[0.08]">
            <ImagePlus className="h-6 w-6 text-gold" />
            {files.length ? u.change : u.choose}
            <input type="file" accept="image/*" multiple onChange={pick} className="sr-only" data-testid="results-upload-file" />
          </label>
          {previews.length > 0 && (
            <div className="grid grid-cols-4 gap-2" data-testid="results-upload-previews">
              {previews.map(({ f, url }) => (
                <div key={url} className="relative aspect-square overflow-hidden rounded-xl border border-white/10">
                  <img src={url} alt="" className="h-full w-full object-cover" />
                  <button type="button" aria-label="remove" onClick={() => setFiles((l) => l.filter((x) => x !== f))} className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <input maxLength={280} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={u.caption} className={field} data-testid="results-upload-caption" />
          <input maxLength={80} value={author} onChange={(e) => setAuthor(e.target.value)} placeholder={u.author} className={field} data-testid="results-upload-author" />
          <button disabled={!files.length || progress !== null} data-testid="results-upload-submit" className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-gold text-sm font-semibold text-ink transition-opacity hover:opacity-90 disabled:opacity-50">
            {progress !== null ? <><Loader2 className="h-4 w-4 animate-spin" /> {u.uploading} {progress}/{files.length}</> : u.submit}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
